import { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";

import {
  AutoAwesome as SparkleIcon,
  CheckCircleOutline as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { grey, green } from "@mui/material/colors";
import { isDev } from "src/helpers/env";
import { snakeToTitleCase } from "src/reusable/Functions";
import { formatToolDataForDisplay } from "./formatToolDataForDisplay";
import { useToolRunElapsed } from "./useToolRunElapsed";

interface ArtifactCardProps {
  toolName: string;
  isLoading: boolean;
  /** When true, visually connect to a follow-up footer row (e.g., completion message) */
  isConnected?: boolean;
  /** Custom status message from the model (e.g., "Searching for safety procedures...") */
  statusMessage?: string;
  /** Full tool call arguments — shown in dev mode for debugging */
  arguments?: Record<string, any>;
  /**
   * Stable identity of this tool run (`${messageId}-${partIndex}`) so the
   * running timer survives unmount/remount (e.g. Equipment AI's tab rail).
   */
  runKey?: string;
}

/**
 * ArtifactCard renders the header of a tool usage block.
 * Shows tool name, loading animation, and status message.
 * In dev mode, shows expandable details of the exact function call.
 */
const ArtifactCard = ({
  toolName,
  isLoading,
  isConnected = false,
  statusMessage,
  arguments: toolArguments,
  runKey,
}: ArtifactCardProps) => {
  const [devExpanded, setDevExpanded] = useState(false);
  const elapsed = useToolRunElapsed(runKey, isLoading);

  // Build the display label excluding status_message from args
  const { status_message: _sm, ...displayArgs } = toolArguments ?? {};

  const baseLabel = statusMessage ?? snakeToTitleCase(toolName);
  const statusLabel = isLoading ? `${baseLabel}...` : baseLabel;

  return (
    <Box
      data-testid={`aa-tool-artifact-${toolName}`}
      sx={{
        borderRadius: isConnected ? "6px 6px 0 0" : "6px",
        bgcolor: grey[100],
        border: `1px solid ${grey[300]}`,
        width: "100%",
        transition: "all 0.15s ease",
        mb: isConnected ? 0 : 1,
      }}
    >
      {/* Main status row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.75,
        }}
      >
        {isLoading ? (
          <SparkleIcon
            sx={{
              fontSize: 14,
              color: "#009688",
              animation: "sparkle 1.5s ease-in-out infinite",
              "@keyframes sparkle": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.5, transform: "scale(0.85)" },
              },
            }}
          />
        ) : (
          <CheckIcon sx={{ fontSize: 14, color: green[500] }} />
        )}
        <Typography
          variant="caption"
          title={statusLabel}
          sx={{
            color: grey[700],
            fontWeight: 500,
            fontSize: "0.813rem",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {statusLabel}
          {isLoading && elapsed > 0 && (
            <Typography
              component="span"
              variant="caption"
              sx={{ color: grey[400], fontWeight: 400, ml: 0.5 }}
            >
              {elapsed}s
            </Typography>
          )}
        </Typography>

        {/* Dev mode expand toggle */}
        {isDev && toolArguments && (
          <Box
            onClick={() => setDevExpanded((prev) => !prev)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: grey[400],
              "&:hover": { color: grey[600] },
            }}
          >
            {devExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 14 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        )}
      </Box>

      {/* Dev mode: full function call details */}
      {isDev && toolArguments && (
        <Collapse in={devExpanded}>
          <Box
            sx={{
              borderTop: `1px solid ${grey[200]}`,
              px: 1.5,
              py: 1,
              bgcolor: grey[50],
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: grey[500],
                fontSize: "0.7rem",
                fontFamily: "monospace",
                mb: 0.5,
              }}
            >
              {toolName}({Object.keys(displayArgs).length > 0 ? "..." : ""})
            </Typography>
            {statusMessage && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: grey[500],
                  fontSize: "0.7rem",
                  mb: 0.5,
                }}
              >
                status_message: &quot;{statusMessage}&quot;
              </Typography>
            )}
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                lineHeight: 1.4,
                color: grey[600],
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
              }}
            >
              {formatToolDataForDisplay(displayArgs)}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ArtifactCard;
